/**
 * Safari-safe blob URL management utilities
 *
 * Safari has specific timing requirements for blob URL cleanup that can cause
 * WebKitBlobResource errors if not handled properly. These utilities provide
 * a consistent pattern for managing blob URLs safely across all browsers.
 */

/**
 * Configuration for Safari-safe cleanup delays
 */
const SAFARI_CLEANUP_DELAY = 3000; // 3 seconds - safe for Safari's async operations

/**
 * Interface for managing a single blob URL with Safari-safe cleanup
 */
export interface SafeBlobUrlManager {
    /** The current blob URL, or null if cleaned up */
    readonly url: string | null;
    /** Whether cleanup has been performed */
    readonly isCleanedUp: boolean;
    /** Perform immediate cleanup (for error cases) */
    cleanup: () => void;
    /** Perform Safari-safe delayed cleanup */
    safeCleanup: () => void;
}

/**
 * Creates a Safari-safe blob URL manager
 */
export function createSafeBlobUrl(file: File): SafeBlobUrlManager {
    let url: string | null = URL.createObjectURL(file);
    let isCleanedUp = false;

    const cleanup = () => {
        if (isCleanedUp || !url) return;
        isCleanedUp = true;

        const urlToRevoke = url;
        url = null;

        try {
            URL.revokeObjectURL(urlToRevoke);
        } catch (error) {
            console.warn("Error revoking blob URL:", error);
        }
    };

    const safeCleanup = () => {
        if (isCleanedUp || !url) return;
        isCleanedUp = true;

        const urlToRevoke = url;
        url = null;

        // Delay revocation for Safari
        setTimeout(() => {
            try {
                URL.revokeObjectURL(urlToRevoke);
            } catch (error) {
                console.warn("Error revoking blob URL during safe cleanup:", error);
            }
        }, SAFARI_CLEANUP_DELAY);
    };

    return {
        get url() {
            return url;
        },
        get isCleanedUp() {
            return isCleanedUp;
        },
        cleanup,
        safeCleanup
    };
}

/**
 * Interface for Safari-safe video element management
 */
export interface SafeVideoElementManager {
    /** The video element */
    readonly element: HTMLVideoElement;
    /** The blob URL manager */
    readonly blobManager: SafeBlobUrlManager;
    /** Whether cleanup has been performed */
    readonly isCleanedUp: boolean;
    /** Perform Safari-safe cleanup of both video element and blob URL */
    cleanup: () => void;
}

/**
 * Creates a Safari-safe video element with blob URL management
 */
export function createSafeVideoElement(
    file: File,
    options?: { muted?: boolean; playsInline?: boolean; crossOrigin?: string; preload?: string }
): SafeVideoElementManager {
    const video = document.createElement("video");
    const blobManager = createSafeBlobUrl(file);
    let isCleanedUp = false;

    // Apply options
    if (options?.muted !== false) video.muted = true;
    if (options?.playsInline !== false) video.playsInline = true;
    if (options?.crossOrigin) video.crossOrigin = options.crossOrigin;
    if (options?.preload) video.preload = options.preload;

    // Set video source
    if (blobManager.url) {
        video.src = blobManager.url;
    }

    const cleanup = () => {
        if (isCleanedUp) return;
        isCleanedUp = true;

        // Clear event listeners to prevent further calls
        video.onloadedmetadata = null;
        video.onerror = null;
        video.onseeked = null;
        video.oncanplay = null;

        // Safari-friendly cleanup: clear src first, then delay removal
        video.src = "";
        video.load(); // Force Safari to stop processing the old source

        // Schedule delayed cleanup for both video element and blob URL
        setTimeout(() => {
            try {
                video.remove();
            } catch (error) {
                console.warn("Error removing video element:", error);
            }
        }, SAFARI_CLEANUP_DELAY);

        // Clean up blob URL with Safari-safe delay
        blobManager.safeCleanup();
    };

    return {
        get element() {
            return video;
        },
        get blobManager() {
            return blobManager;
        },
        get isCleanedUp() {
            return isCleanedUp;
        },
        cleanup
    };
}

/**
 * Promise-based video loading with Safari-safe cleanup
 */
export async function loadVideoSafely(
    file: File,
    options?: {
        muted?: boolean;
        playsInline?: boolean;
        crossOrigin?: string;
        preload?: string;
        timeout?: number;
    }
): Promise<SafeVideoElementManager> {
    const manager = createSafeVideoElement(file, options);
    const timeout = options?.timeout || 5000;

    return new Promise((resolve, reject) => {
        const cleanup = () => {
            manager.cleanup();
        };

        manager.element.onloadedmetadata = () => {
            resolve(manager);
        };

        manager.element.onerror = (error) => {
            cleanup();
            reject(new Error("Failed to load video"));
        };

        // Timeout fallback
        setTimeout(() => {
            if (!manager.isCleanedUp) {
                cleanup();
                reject(new Error("Video load timeout"));
            }
        }, timeout);

        manager.element.load();
    });
}
